import { CitySelector, CitySelectorProps } from '@components/CitySelector/CitySelector'
import { createToken } from '@components/CitySelector/stateManagement'
import { useLocationSuggestion } from '@data/hooks/useLocationSuggestion'
import { useLocationSuggestionPlace } from '@data/hooks/useLocationSuggestionPlace'
import locationSchema from '@data/schemas/location'
import placeSchema from '@data/schemas/place'
import { TextField } from '@internations/ds'
import { useField } from '@util/Formik'
import { ReactNode, useMemo, useRef, useState } from 'react'

type Props = CitySelectorProps & {
    /**
     * Formik field name.
     */
    fieldName: string

    /**
     * Unlike the standard `error`, this is displayed regardless of whether the user has touched the field yet.
     */
    forcedError?: string
}

type InitialValue = [value: TPlace | TLocation | undefined, isReady: boolean]

function humanFriendlyString(value: TPlace | TLocation): string {
    return `${value.city}, ${value.country.name}`
}

export const useInitialValue = (initialValue?: TPlace | TLocation | string): InitialValue => {
    const isInitialValueEmpty = !initialValue
    const isInitialValueAPlace = placeSchema.isValidSync(initialValue)
    const isInitialValueALocation = locationSchema.isValidSync(initialValue)
    const query =
        typeof initialValue === 'string'
            ? initialValue
            : isInitialValueALocation
              ? humanFriendlyString(initialValue)
              : ''
    const sessionToken = useMemo(createToken, [initialValue])
    const { data: locationSuggestions, error: locationSuggestionsError } = useLocationSuggestion(
        { query, sessionToken },
        {
            enabled: !isInitialValueAPlace && Boolean(query) && !isInitialValueEmpty,
        }
    )
    const suggestion = locationSuggestions.length > 0 ? locationSuggestions[0] : undefined
    const { data: selectedCity, error: selectedCityError } = useLocationSuggestionPlace(suggestion?.placeId, {
        enabled: Boolean(suggestion?.placeId) && !isInitialValueAPlace,
    })
    const value = isInitialValueAPlace ? initialValue : selectedCity
    const isReady =
        isInitialValueEmpty ||
        isInitialValueAPlace ||
        Boolean(selectedCity && !locationSuggestionsError && !selectedCityError)

    return [value, isReady]
}

function getPlaceholder({
    value,
    isReady,
    label,
    rest,
}: {
    value: InitialValue[0]
    isReady: boolean
    label: Props['label']
    rest: Omit<Props, 'fieldName' | 'label' | 'backendError'>
}): Props['placeholder'] {
    if (isReady) {
        return rest.placeholder
    }

    if (value?.city) {
        return humanFriendlyString(value)
    }

    return label
}

function getErrorMessage({
    value,
    forcedError,
    touched,
    error,
    hasBeenCleared,
}: {
    value: InitialValue[0]
    forcedError: Props['forcedError']
    touched: boolean
    error: string | undefined
    hasBeenCleared: boolean
}) {
    if (forcedError) {
        return forcedError
    }

    if (value || touched || hasBeenCleared) {
        return error
    }
}

/**
 * Wrapper for `<CitySelector>` that makes it work automatically with Formik fields.
 *
 * Note that the field's type must be `TPlace | undefined` and there is no type-safety to prevent you from using another type.
 */
export const FormikCitySelector = ({ fieldName, label, forcedError, className, ...rest }: Props): ReactNode => {
    const [{ value }, { touched, error, initialValue }, { setTouched, setValue }] = useField<
        TPlace | TLocation | undefined
    >(fieldName)
    const initialFormValueRef = useRef(value || initialValue)
    const [initialPlaceValue, isReady] = useInitialValue(initialFormValueRef.current)
    const placeholder = getPlaceholder({ value, isReady, label, rest })
    const [hasBeenCleared, setHasBeenCleared] = useState(false)

    if (!isReady) {
        return (
            <TextField
                disabled
                id={fieldName}
                label={label}
                placeholder={placeholder}
                fullWidth={rest.fullWidth}
                onBlur={() => setTouched(true)}
                className={className}
            />
        )
    }

    const onSelect = (city?: TPlace) => setValue(city)
    const onBlur = () => setTouched(true)
    const onClear = () => {
        setValue(undefined)
        setHasBeenCleared(true)
    }

    return (
        <CitySelector
            id={fieldName}
            initialValue={initialPlaceValue}
            label={label}
            error={getErrorMessage({ value, forcedError, touched, error, hasBeenCleared })}
            onSelect={onSelect}
            onBlur={onBlur}
            onClear={onClear}
            className={className}
            {...rest}
            placeholder={placeholder}
        />
    )
}
